import React from 'react';
import { Link } from 'react-router-dom';
import { ParticipationEvent } from '../../../types/participation';

interface ParticipationEventEntryProps {
  event: ParticipationEvent;
  eventTypeColor?: string;
}

// Event type icons matching design spec
const EVENT_TYPE_ICONS: Record<string, string> = {
  'Practice': '🚴',
  'Race': '🏁',
  'Time Trial': '⏱️',
  'Meeting': '📋',
  'Social': '🎉',
  'default': '📅',
};

/**
 * Format date as "MMM DD, YYYY" (e.g., "Jan 15, 2025")
 */
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
}

/**
 * Format time as "H:MM AM/PM" (e.g., "3:30 PM")
 */
function formatTime(datetimeString: string): string {
  try {
    const date = new Date(datetimeString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '';
  }
}

/**
 * Format duration as "X.X hrs" (e.g., "2.5 hrs")
 */
function formatDuration(hours: number): string {
  return `${hours.toFixed(1)} hrs`;
}

/**
 * ParticipationEventEntry Component
 * 
 * Displays a single event entry in the participation history list
 * Shows event details, duration, and optionally time trial results
 */
export const ParticipationEventEntry: React.FC<ParticipationEventEntryProps> = ({ event, eventTypeColor }) => {
  const isCanceled = event.event_status === 'canceled';
  const eventColor = eventTypeColor || '#435d7d'; // Default to primary blue
  const eventTypeIcon = EVENT_TYPE_ICONS[event.event_type] || EVENT_TYPE_ICONS.default;
  
  // For canceled events, always show 0.0 hours
  const displayDuration = isCanceled ? 0.0 : event.duration_hours;
  
  return (
    <div
      className={`
        border rounded-lg p-3 sm:p-4 mb-3
        ${isCanceled ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200'}
        hover:shadow-md transition-shadow
      `}
    >
      {/* Mobile: Stack vertically, Desktop: Side by side */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        {/* Left side: Event info */}
        <div className="flex-1 min-w-0">
          {/* Event name and icon */}
          <div className="flex items-start gap-2 mb-2">
            <span className="text-xl flex-shrink-0">{eventTypeIcon}</span>
            <div className="flex-1 min-w-0">
              <Link
                to={`/events/${event.event_id}`}
                className={`
                  text-base sm:text-lg font-semibold hover:underline block
                  ${isCanceled ? 'text-gray-500 line-through' : 'text-blue-600'}
                  truncate
                `}
                title={event.event_name}
              >
                {event.event_name}
              </Link>
              {/* Badges on separate line for mobile */}
              <div className="flex flex-wrap gap-1 mt-1">
                <span
                  className="px-2 py-1 rounded text-xs font-medium text-white whitespace-nowrap"
                  style={{ backgroundColor: eventColor }}
                >
                  {event.event_type}
                </span>
                {isCanceled && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-400 text-white whitespace-nowrap">
                    Canceled
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Date and time */}
          <div className={`text-xs sm:text-sm ${isCanceled ? 'text-gray-500' : 'text-gray-600'} mb-1 ml-8`}>
            📅 {formatDate(event.event_date)} • 🕐 {formatTime(event.event_starttime)}
          </div>
          
          {/* Time trial results (if applicable) */}
          {event.time_trial_result && !isCanceled && (
            <div className="mt-2 ml-8 p-2 bg-orange-50 border border-orange-200 rounded text-xs sm:text-sm">
              <div className="font-medium text-orange-900">
                ⏱️ Time Trial Result: {event.time_trial_result.formatted_time}
              </div>
              <div className="text-orange-700 text-xs mt-1 break-words">
                {event.time_trial_result.trail_name} • {event.time_trial_result.direction}
              </div>
            </div>
          )}
        </div>
        
        {/* Right side: Duration */}
        <div className="text-center sm:text-right sm:ml-4 flex-shrink-0">
          <div className={`text-xl sm:text-2xl font-bold ${isCanceled ? 'text-gray-400' : 'text-blue-600'}`}>
            {formatDuration(displayDuration)}
          </div>
          <div className={`text-xs ${isCanceled ? 'text-gray-400' : 'text-gray-500'}`}>
            Duration
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipationEventEntry;
